// Pure slot/availability helpers for ActivityWizard Steg 2 (delspår 3c).
//
// Read-only preview logic — the AUTHORITATIVE court-availability check happens at
// activity creation (delspår 3g) via validateCourtAvailability + the DB trigger
// trg_bookings_no_overlap. This module mirrors the bookings-overlap portion for a
// fast client-side slot preview (one bookings-query per date, no per-slot async).
// Recurring blocks + league matches are NOT considered here (3g/DB-trigger guard them).
//
// Times are Stockholm-local "HH:mm" wall-time. The consumer converts to absolute
// ISO for overlap comparison (browser-local == Stockholm for Boply users).

export interface DayCourtBooking {
  court_id: string;
  start_time: string; // ISO (absolute)
  end_time: string; // ISO (absolute)
}

export type CourtMode = "none" | "auto" | "specific";

export function hhmmToMinutes(t: string): number {
  const [h, m] = t.slice(0, 5).split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

export function minutesToHHMM(min: number): string {
  const h = Math.floor(min / 60) % 24;
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Duration pills: min→max in `step` increments (e.g. 30/60/90/120 for demo). */
export function durationOptions(min: number, max: number, step: number): number[] {
  const out: number[] = [];
  const s = step > 0 ? step : 30;
  for (let d = min; d <= max; d += s) out.push(d);
  return out;
}

/**
 * Candidate start times "HH:mm" from opening to closing, such that
 * start + durationMin <= closing. `closingMin === 0` means midnight (24:00).
 */
export function generateStartTimes(
  openingMin: number,
  closingMinRaw: number,
  stepMin: number,
  durationMin: number,
): string[] {
  const closingMin = closingMinRaw === 0 ? 24 * 60 : closingMinRaw;
  const step = stepMin > 0 ? stepMin : 30;
  const out: string[] = [];
  for (let t = openingMin; t + durationMin <= closingMin; t += step) {
    out.push(minutesToHHMM(t));
  }
  return out;
}

function courtHasOverlap(
  courtId: string,
  startISO: string,
  endISO: string,
  bookings: DayCourtBooking[],
): boolean {
  // Compare instants via epoch ms, NOT raw ISO strings. Booking times come from Postgres
  // ("...+00:00") while slot times come from JS Date.toISOString() ("...000Z"); the SAME
  // instant serialises to different strings, and a lexical compare gets boundaries wrong
  // (e.g. "...06:00:00+00:00" < "...06:00:00.000Z" is true because '+' < '.'). That made a
  // slot ending exactly when a booking starts (back-to-back) read as overlapping → hidden.
  // Half-open intervals [start, end): touching at a boundary is NOT an overlap.
  const startMs = new Date(startISO).getTime();
  const endMs = new Date(endISO).getTime();
  return bookings.some((b) => {
    if (b.court_id !== courtId) return false;
    return new Date(b.start_time).getTime() < endMs && new Date(b.end_time).getTime() > startMs;
  });
}

/**
 * Is a slot selectable given the court-mode?
 * - none: always (no court reserved, only open-hours bound by generateStartTimes)
 * - specific: the selected court must be free (if none picked yet → treat as free)
 * - auto: at least one of allCourtIds must be free
 */
export function isSlotAvailable(
  startISO: string,
  endISO: string,
  mode: CourtMode,
  allCourtIds: string[],
  selectedCourtId: string | null,
  bookings: DayCourtBooking[],
): boolean {
  if (mode === "none") return true;
  if (mode === "specific") {
    if (!selectedCourtId) return true;
    return !courtHasOverlap(selectedCourtId, startISO, endISO, bookings);
  }
  // auto
  return allCourtIds.some((c) => !courtHasOverlap(c, startISO, endISO, bookings));
}

// ── Multi-court availability (delspår multi-bana, Fas 2a) ───────────────────────
// Additive layer for N-court activities, where N is derived from the player count.
// The legacy isSlotAvailable above is UNTOUCHED (TimeSlotGrid still uses it until 2b
// wires the multi variant in). All overlap math reuses courtHasOverlap → inherits the
// epoch-compare boundary semantics [).

/** Padel standard: 4 players per court. Courts below this are excluded from the auto pool. */
export const PADEL_MIN_COURT_CAPACITY = 4;

/**
 * How many of the given courts are FULLY free during [startISO, endISO).
 * Reuses courtHasOverlap (epoch compare) — never string-compares ISO.
 */
export function countFreeCourts(
  startISO: string,
  endISO: string,
  courtIds: string[],
  bookings: DayCourtBooking[],
): number {
  return courtIds.filter((courtId) => !courtHasOverlap(courtId, startISO, endISO, bookings)).length;
}

/** N courts required for `spotsTotal` players (padel standard 4/court; ceil is defensive). */
export function deriveRequiredCourts(spotsTotal: number): number {
  return Math.max(1, Math.ceil(spotsTotal / PADEL_MIN_COURT_CAPACITY));
}

/**
 * Auto-pool court ids: active courts with capacity >= 4 (excludes 2-player courts).
 * The 2-player court is never auto-assigned; the caller may still offer it as a
 * specific 1-court choice if product allows (out of scope for the auto pool).
 */
export function filterAutoPoolCourtIds(
  courts: { id: string; max_players: number; status: string }[],
): string[] {
  return courts
    .filter((c) => c.status === "active" && c.max_players >= PADEL_MIN_COURT_CAPACITY)
    .map((c) => c.id);
}

/**
 * N-court-aware availability. Additive sibling of isSlotAvailable.
 * - none: always selectable (no court reserved)
 * - specific: the N chosen courts must ALL be free (multi-specific; N=1 = single court)
 * - auto: at least `requiredCourts` of the auto pool must be free SIMULTANEOUSLY
 */
export function isSlotAvailableMulti(
  startISO: string,
  endISO: string,
  mode: CourtMode,
  autoPoolCourtIds: string[],
  selectedCourtIds: string[],
  bookings: DayCourtBooking[],
  requiredCourts: number,
): boolean {
  if (mode === "none") return true;
  if (mode === "specific") {
    return (
      selectedCourtIds.length >= requiredCourts &&
      selectedCourtIds.every((c) => !courtHasOverlap(c, startISO, endISO, bookings))
    );
  }
  // auto
  return countFreeCourts(startISO, endISO, autoPoolCourtIds, bookings) >= requiredCourts;
}

/**
 * Pick `count` actually-free courts from the auto pool for the slot (submit-time, delspår 3g).
 * Returns the first `count` free court ids, or null when fewer than `count` are free (the slot
 * filled up since step 3 — the caller treats null as a race → "the time was just taken").
 * Reuses courtHasOverlap (epoch compare). Deterministic order = autoPoolCourtIds order.
 */
export function pickAvailableCourts(
  startISO: string,
  endISO: string,
  autoPoolCourtIds: string[],
  bookings: DayCourtBooking[],
  count: number,
): string[] | null {
  const free = autoPoolCourtIds.filter((c) => !courtHasOverlap(c, startISO, endISO, bookings));
  return free.length >= count ? free.slice(0, count) : null;
}
